/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo, Fragment, Suspense } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { Global, css } from '@emotion/react'

import { EuiSpacer, EuiText, EuiFlexGroup, EuiFlexItem, EuiLoadingSpinner } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { DocLink } from '@modules/doclinks'
import Feature from '@modules/utils/feature'

import {
  getFirstEsClusterFromGet,
  getPlanInfo,
} from '@/lib/stackDeployments/selectors/fundamentals' // eslint-disable-line import/no-restricted-paths
import { isFeatureActivated } from '@/store' // eslint-disable-line import/no-restricted-paths

import { useGetDeploymentQuery } from '../cloud-lib/deployments/hooks'

import LazyOneConsole from './lazy-one-console'
import { createHttpClient } from './http-client'
import { createNotifications } from './notifications'
import { mapLocaleToConsoleLanguage } from './locale-mapper'
import { ConsolePrivacyWarning } from './ConsolePrivacyWarning'

const globalStyles = css`
  #consoleRoot {
    height: calc(100vh - 380px);
    padding: 0;
  }
`

const OneConsole: React.FC = () => {
  const { deploymentId: stackDeploymentId } = useParams<{ deploymentId: string }>()

  const intl = useIntl()
  const { data: deployment } = useGetDeploymentQuery(stackDeploymentId || undefined)

  const http = useMemo(() => createHttpClient(deployment), [deployment])
  const notifications = useMemo(() => createNotifications(), [])
  const selectedLanguage = useMemo(() => mapLocaleToConsoleLanguage(intl.locale), [intl.locale])

  const showSupportInstructions = isFeatureActivated(Feature.showSupportInstructions)

  if (!deployment) {
    return null
  }

  const firstEs = getFirstEsClusterFromGet({ deployment })

  if (firstEs && !getPlanInfo({ resource: firstEs })) {
    return null
  }

  return (
    <Fragment>
      <Global styles={globalStyles} />
      {showSupportInstructions && (
        <Fragment>
          <ConsolePrivacyWarning />
          <EuiSpacer size='m' />
        </Fragment>
      )}
      <EuiText>
        <FormattedMessage
          id='one-console.overview'
          defaultMessage='Perform operations-related tasks from this enhanced console. You can run search queries, review the list of snapshots, check the health of your clusters, and { link }.'
          values={{
            link: (
              <DocLink link='apiConsoleDocLink'>
                <FormattedMessage id='one-console.overview.more' defaultMessage='more' />
              </DocLink>
            ),
          }}
        />
      </EuiText>

      <EuiSpacer size='m' />

      <Suspense
        fallback={
          <EuiFlexGroup justifyContent='center' alignItems='center' style={{ minHeight: '200px' }}>
            <EuiFlexItem grow={false}>
              <EuiLoadingSpinner size='xl' />
            </EuiFlexItem>
          </EuiFlexGroup>
        }
      >
        <LazyOneConsole http={http} lang={selectedLanguage} notifications={notifications} />
      </Suspense>
    </Fragment>
  )
}

export default OneConsole
